import { OnboardingV2DealSizeBucket } from "@redotech/cockroachdb-codegen/tables/internal-ops/onboarding-v2-deal-size-bucket";
import * as z from "zod";

export const getReturnsActivationFunnelInputSchema = z.object({
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
  /** Exclude self-serve rows with <60s recorded step time (matches Funnel). Default: true. */
  filterLowIntent: z.boolean().optional(),
});
export type GetReturnsActivationFunnelInput = z.infer<
  typeof getReturnsActivationFunnelInputSchema
>;

/** Exhaustive over the enum, so a new schema value must be given a position. */
const DEAL_SIZE_BUCKET_DISPLAY_ORDER: Record<
  OnboardingV2DealSizeBucket,
  number
> = {
  [OnboardingV2DealSizeBucket.Under1k]: 0,
  [OnboardingV2DealSizeBucket._1kTo5k]: 1,
  [OnboardingV2DealSizeBucket._5kTo25k]: 2,
  [OnboardingV2DealSizeBucket._25kTo100k]: 3,
  [OnboardingV2DealSizeBucket._100kPlus]: 4,
  [OnboardingV2DealSizeBucket.Unknown]: 5,
};
/** CRDB `onboarding_v2_deal_size_bucket` values, in display order. */
export const RETURNS_DEAL_SIZE_BUCKETS: readonly OnboardingV2DealSizeBucket[] =
  Object.values(OnboardingV2DealSizeBucket).sort(
    (a, b) =>
      DEAL_SIZE_BUCKET_DISPLAY_ORDER[a] - DEAL_SIZE_BUCKET_DISPLAY_ORDER[b],
  );

/**
 * Cohort = loaded self-serve rows in the window, classified by routing. "Went
 * live" = completed + `first_revenue_at` set, including assisted activation.
 */
const routedCohortSchema = z.object({
  /** Rows routed into self-serve (exact + inferred) — the rate denominator. */
  routedSelfServe: z.number().int().min(0),
  routedToCall: z.number().int().min(0),
  /** Completed rows among the routed-self-serve cohort. */
  completed: z.number().int().min(0),
  /** Completed rows with `first_revenue_at IS NOT NULL`. */
  wentLive: z.number().int().min(0),
  /** Routed Cohort Activation Rate: wentLive / routedSelfServe. */
  activationPct: z.number().min(0).max(100),
  /** Auto-push rate over known-mechanism go-lives: autoPushTrue / (autoPushTrue + autoPushFalse); unknowns excluded. */
  autoPushActivationPct: z.number().min(0).max(100),
  /** `autoPushAtCompletion` split among wentLive rows; unknown = historical. */
  autoPushSplit: z.object({
    autoPushTrue: z.number().int().min(0),
    autoPushFalse: z.number().int().min(0),
    autoPushUnknown: z.number().int().min(0),
  }),
});
export type ReturnsRoutedCohortSummary = z.infer<typeof routedCohortSchema>;

/** Weekly `started_at` cohort (UTC weeks) of routed-self-serve rows. */
const weeklyRoutedCohortPointSchema = z.object({
  weekStart: z.date(),
  routedSelfServe: z.number().int().min(0),
  wentLive: z.number().int().min(0),
  activationPct: z.number().min(0).max(100),
  /** Cohort younger than 14 days — later completions can still change it. */
  immature: z.boolean(),
});
export type ReturnsWeeklyRoutedCohortPoint = z.infer<
  typeof weeklyRoutedCohortPointSchema
>;

const weeklySelfServeMetricsPointSchema = z.object({
  weekStart: z.date(),
  selfServeBrandCount: z.number().int().min(0),
  orderVolume30d: z.number().int().min(0),
  realizedRevenue30dCents: z.string(),
  rolling4WeekAvgBrandCount: z.number().min(0),
  rolling4WeekAvgOrderVolume30d: z.number().min(0),
  rolling4WeekAvgRevenue30dCents: z.string(),
});
export type ReturnsWeeklySelfServeMetricsPoint = z.infer<
  typeof weeklySelfServeMetricsPointSchema
>;

/** Routed-self-serve cohort per deal-size bucket; exhaustive, zero-filled. */
const routedBucketRowSchema = z.object({
  bucket: z.enum(OnboardingV2DealSizeBucket),
  started: z.number().int().min(0),
  completed: z.number().int().min(0),
  wentLive: z.number().int().min(0),
  activationPct: z.number().min(0).max(100),
});
export type ReturnsRoutedBucketRow = z.infer<typeof routedBucketRowSchema>;

/**
 * Routed-to-call counts. `exact` = persisted routing decisions or client-observed
 * booking evidence; `inferred` = historical signals and current Team volume.
 */
const routedToCallReasonsSchema = z.object({
  exact: z.object({
    volumeAtOrAboveThreshold: z.number().int().min(0),
    provisionalVolumeAtOrAboveThreshold: z.number().int().min(0),
    volumeUnavailableAfterPolling: z.number().int().min(0),
    /** Client-observed HubSpot meeting booking on the schedule-call step. */
    clientObservedBooking: z.number().int().min(0),
  }),
  inferred: z.object({
    exitModalHelp: z.number().int().min(0),
    /** Schedule-call visit + current volume >= threshold. */
    threshold: z.number().int().min(0),
    /** Schedule-call visit + current volume < threshold — likely null volume at routing time. */
    possibleNullVolume: z.number().int().min(0),
    /** Schedule-call visit with no current volume available. */
    dataMissing: z.number().int().min(0),
  }),
});
export type ReturnsRoutedToCallReasons = z.infer<
  typeof routedToCallReasonsSchema
>;

export const returnsActivationFunnelOutputSchema = z.object({
  routedCohort: routedCohortSchema,
  /** Weekly routed cohorts (current window, ascending week). */
  weeklyCohorts: z.array(weeklyRoutedCohortPointSchema),
  /** Completed self-serve cohorts by UTC completion week. Revenue/order fields are 30-day post-completion values. */
  weeklySelfServeMetrics: z.array(weeklySelfServeMetricsPointSchema),
  /** Routed-self-serve cohort per deal-size bucket (current window). */
  byBucket: z.array(routedBucketRowSchema),
  /** Routed-to-call reason counts (current window). */
  routedToCallReasons: routedToCallReasonsSchema,
});
export type GetReturnsActivationFunnelOutput = z.infer<
  typeof returnsActivationFunnelOutputSchema
>;

export const getReturnsActivationFunnel = {
  name: "getReturnsActivationFunnel" as const,
  input: getReturnsActivationFunnelInputSchema,
  output: returnsActivationFunnelOutputSchema,
};
